import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * In-drawer fast add for cart upsells.
 *
 * Single-variant products are added to the cart directly from the "Add +" button.
 * Multi-variant products open a compact in-drawer variant picker panel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content
 * @property {HTMLElement} title
 * @property {HTMLButtonElement} closeButton
 *
 * @typedef {object} ProductVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price
 * @property {number|null} compare_at_price
 *
 * @typedef {object} ProductJson
 * @property {number} id
 * @property {string} title
 * @property {string} handle
 * @property {ProductVariant[]} variants
 * @property {{name: string, position: number, values: string[]}[]} options
 *
 * @extends {Component<Refs>}
 */
class CartDrawerFastAdd extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, ProductJson>} */
  #productCache = new Map();

  /** @type {ProductJson | null} */
  #activeProduct = null;

  /** @type {HTMLElement | null} */
  #triggerButton = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    document.addEventListener('keydown', this.#handleKeydown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener('keydown', this.#handleKeydown, true);
  }

  get isOpen() {
    return !this.hasAttribute('hidden');
  }

  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (this.isOpen) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  };

  /**
   * Entry point for the upsell "Add +" buttons.
   * @param {Event} event
   */
  openFromButton = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.cart-drawer-upsell__add');
    const item = target?.closest('.cart-drawer-upsell__item');
    const link = item?.querySelector('.cart-drawer-upsell__product-link');
    const handle =
      link?.getAttribute('data-product-handle') ||
      (link instanceof HTMLAnchorElement ? this.#getHandleFromUrl(link.href) : null);

    if (!handle || button?.classList.contains('is-loading')) return;

    const titleEl = item?.querySelector('.cart-drawer-upsell__title');
    const productTitle = titleEl?.textContent?.trim() || '';

    this.#triggerButton = button instanceof HTMLElement ? button : null;
    button?.classList.add('is-loading');

    try {
      const product = await this.#fetchProduct(handle);
      if (!product) return;

      this.#activeProduct = product;
      const purchasable = product.variants.filter((variant) => variant.available);

      if (purchasable.length === 0) {
        this.#openPanel(productTitle);
        this.#showError('This product is currently sold out.');
        return;
      }

      const isSingleVariant = product.variants.length === 1 || purchasable[0]?.title === 'Default Title';

      if (isSingleVariant) {
        // True fast add: no panel, straight to cart.
        const ok = await this.#addToCart(purchasable[0].id);
        if (!ok) {
          this.#openPanel(productTitle);
          this.#renderProduct(product);
        }
      } else {
        this.#openPanel(productTitle);
        this.#renderProduct(product);
      }
    } catch (error) {
      console.error(error);
      this.#openPanel(productTitle);
      this.#showError('Unable to load product options.');
    } finally {
      button?.classList.remove('is-loading');
    }
  };

  /** @param {string} productTitle */
  #openPanel(productTitle) {
    this.toggleAttribute('hidden', false);
    if (this.refs.title) this.refs.title.textContent = productTitle;
    if (this.refs.content) {
      this.refs.content.replaceChildren();
      this.refs.content.classList.remove('cart-drawer-fast-add__content--error');
    }

    requestAnimationFrame(() => {
      const sheet = this.querySelector('.cart-drawer-fast-add__sheet');
      if (sheet instanceof HTMLElement) sheet.focus({ preventScroll: true });
    });
  }

  close = () => {
    this.toggleAttribute('hidden', true);
    this.#activeProduct = null;
    this.refs.content?.replaceChildren();
    this.refs.content?.classList.remove('cart-drawer-fast-add__content--error');

    if (this.#triggerButton?.isConnected) {
      this.#triggerButton.focus({ preventScroll: true });
    }
    this.#triggerButton = null;
  };

  /** @param {string} message */
  #showError(message) {
    if (!this.refs.content) return;
    this.refs.content.replaceChildren();
    this.refs.content.classList.add('cart-drawer-fast-add__content--error');
    const error = document.createElement('p');
    error.className = 'cart-drawer-fast-add__error';
    error.textContent = message;
    this.refs.content.appendChild(error);
  }

  /** @param {ProductJson} product */
  #renderProduct(product) {
    if (!this.refs.content) return;

    const availableVariants = product.variants.filter((variant) => variant.available);
    this.refs.content.replaceChildren();
    this.refs.content.classList.remove('cart-drawer-fast-add__content--error');

    if (availableVariants.length === 0) {
      this.#showError('This product is currently sold out.');
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-drawer-fast-add__picker';

    let selectedVariant = availableVariants[0];

    const optionLabel = document.createElement('p');
    optionLabel.className = 'cart-drawer-fast-add__option-label';
    optionLabel.textContent = product.options[0]?.name || 'Choose option';
    wrapper.appendChild(optionLabel);

    const grid = document.createElement('div');
    grid.className = 'cart-drawer-fast-add__option-grid';
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', optionLabel.textContent);

    product.variants.forEach((variant) => {
      const label = document.createElement('label');
      label.className = 'cart-drawer-fast-add__option';
      if (!variant.available) label.classList.add('is-unavailable');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'cart-drawer-fast-add-variant';
      input.value = String(variant.id);
      input.checked = variant.id === selectedVariant.id;
      input.disabled = !variant.available;

      const text = document.createElement('span');
      text.textContent = variant.available ? variant.title : `${variant.title} · Sold out`;

      label.appendChild(input);
      label.appendChild(text);
      grid.appendChild(label);
    });

    grid.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      const next = product.variants.find((variant) => variant.id === Number(target.value));
      if (next) {
        selectedVariant = next;
        this.#updatePriceAndButton(selectedVariant, priceEl, submitBtn);
      }
    });

    wrapper.appendChild(grid);

    const priceEl = document.createElement('p');
    priceEl.className = 'cart-drawer-fast-add__price';
    wrapper.appendChild(priceEl);

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'cart-drawer-fast-add__submit';
    submitBtn.addEventListener('click', async () => {
      if (!selectedVariant?.available || submitBtn.disabled) return;
      const originalLabel = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';
      const ok = await this.#addToCart(selectedVariant.id);
      if (!ok && submitBtn.isConnected) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    });
    wrapper.appendChild(submitBtn);

    this.refs.content.appendChild(wrapper);
    this.#updatePriceAndButton(selectedVariant, priceEl, submitBtn);
  }

  /**
   * @param {ProductVariant} variant
   * @param {HTMLElement} priceEl
   * @param {HTMLButtonElement} submitBtn
   */
  #updatePriceAndButton(variant, priceEl, submitBtn) {
    const formattedPrice = this.#formatPrice(variant.price);
    const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

    priceEl.replaceChildren();
    const now = document.createElement('span');
    now.className = 'cart-drawer-fast-add__price-now';
    now.textContent = formattedPrice;
    priceEl.appendChild(now);

    if (hasCompare && variant.compare_at_price) {
      const was = document.createElement('s');
      was.className = 'cart-drawer-fast-add__price-was';
      was.textContent = this.#formatPrice(variant.compare_at_price);
      priceEl.appendChild(was);
    }

    submitBtn.disabled = !variant.available;
    submitBtn.textContent = variant.available ? `Add to cart — ${formattedPrice}` : 'Sold out';
  }

  /**
   * Adds the variant to the cart and refreshes the drawer via the section rendering API.
   * @param {number} variantId
   * @returns {Promise<boolean>} success
   */
  async #addToCart(variantId) {
    const formData = new FormData();
    formData.set('id', String(variantId));
    formData.set('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId && !sectionIds.includes(item.dataset.sectionId)) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Unable to add to cart.');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-drawer-fast-add', {
          source: 'cart-drawer-fast-add',
          variantId: String(variantId),
          itemCount: 1,
          productId: this.#activeProduct ? String(this.#activeProduct.id) : undefined,
          sections: data.sections,
        })
      );

      if (this.isOpen) this.close();
      return true;
    } catch (error) {
      console.error(error);
      if (this.isOpen) {
        this.#showError(error instanceof Error ? error.message : 'Unable to add to cart.');
      }
      return false;
    }
  }

  /**
   * @param {string} handle
   * @returns {Promise<ProductJson | null>}
   */
  async #fetchProduct(handle) {
    if (this.#productCache.has(handle)) {
      return this.#productCache.get(handle) ?? null;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(`/products/${handle}.js`, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: HTTP ${response.status}`);
      }

      const product = /** @type {ProductJson} */ (await response.json());
      this.#productCache.set(handle, product);
      return product;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /** @param {string} url */
  #getHandleFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  /**
   * Formats money using the shop's money format so output matches the Liquid
   * `money` filter (e.g. "$79.99") instead of the browser-locale Intl output.
   * @param {number} cents
   */
  #formatPrice(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const format = this.dataset.moneyFormat || '${{amount}}';

    try {
      return formatMoney(cents, format, currency);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cart-drawer-fast-add')) {
  customElements.define('cart-drawer-fast-add', CartDrawerFastAdd);
}
